/**
 * Every player's asset-package download state for a game, so an admin can see
 * who has the round's maps before starting it.
 *
 * Pushed on `ASSETS_STATUS` whenever a player reports progress; requested once on
 * mount and on reconnect. Never polled.
 */

import { useEffect, useState } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import {
  SocketEvent,
  type AssetsRequestStatusRequest,
  type AssetsRequestStatusResponse,
  type AssetsStatusBroadcast,
} from '@swtorbit/shared-socket-events';

export function useAssetStatus(roomCode: string | null): AssetsStatusBroadcast | null {
  const { socket } = useSocketContext();
  const [status, setStatus] = useState<AssetsStatusBroadcast | null>(null);

  useEffect(() => {
    setStatus(null);
    if (!socket || !roomCode) return;

    const apply = (payload: AssetsStatusBroadcast) => {
      if (payload.roomCode === roomCode) setStatus(payload);
    };
    const request = () => {
      socket.emit(
        SocketEvent.ASSETS_REQUEST_STATUS,
        { roomCode } satisfies AssetsRequestStatusRequest,
        (res: AssetsRequestStatusResponse) => {
          if (res?.success && res.state) apply(res.state);
        },
      );
    };

    socket.on(SocketEvent.ASSETS_STATUS, apply);
    socket.on('connect', request);
    request();
    return () => {
      socket.off(SocketEvent.ASSETS_STATUS, apply);
      socket.off('connect', request);
    };
  }, [socket, roomCode]);

  return status;
}
